import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { canMenu } from "../lib/menuPermissions";
import { Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard, BookMarked, HardHat, Menu, X, LogOut, Users, Settings, ContactRound, Mail, MessageCircle, Building2, Maximize2, Minimize2, Landmark, PanelLeftClose, PanelLeftOpen, BriefcaseBusiness, Truck, FileText, RefreshCw, Loader2, House,
} from "lucide-react";

const NAV = [
  { to: "/", permission: "home", label: "La mia giornata", icon: House },
  { to: "/control-room", permission: "control-room", label: "Control Room", icon: LayoutDashboard },
  { to: "/clienti", permission: "customers", label: "Clienti", icon: BriefcaseBusiness },
  { to: "/offerte", permission: "offers", label: "Offerte", icon: FileText },
  { to: "/contabilita-cantieri", permission: "accounting", label: "Contabilità cantieri", icon: BriefcaseBusiness },
  { to: "/cantieri", permission: "sites", label: "Cantieri", icon: HardHat },
  { to: "/fornitori", permission: "suppliers", label: "Fornitori", icon: Truck },
  { to: "/gare", permission: "tenders", label: "Gare", icon: Landmark },
  { to: "/operai", permission: "workers", label: "Operai", icon: Users },
  { to: "/contatti", permission: "contacts", label: "Contatti", icon: ContactRound },
  { to: "/email", permission: "emails", label: "Email", icon: Mail },
  { to: "/whatsapp", permission: "whatsapp", label: "WhatsApp", icon: MessageCircle, adminOnly: true },
  { to: "/documenti", permission: "documents", label: "Documenti", icon: Building2 },
  { to: "/prezzari", permission: "pricebooks", label: "Prezzari Regionali", icon: BookMarked },
  { to: "/impostazioni", permission: "settings", label: "Impostazioni", icon: Settings },
];

export default function Layout({ children, onLogout, session }) {
  const [open, setOpen] = useState(false);
  const [embedded, setEmbedded] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(Boolean(document.fullscreenElement));
  const [syncDocsBusy, setSyncDocsBusy] = useState(false);
  const fullscreenSupported = Boolean(document.documentElement.requestFullscreen);
  useEffect(() => { const fn=()=>setIsFullscreen(Boolean(document.fullscreenElement)); document.addEventListener("fullscreenchange",fn); return()=>document.removeEventListener("fullscreenchange",fn); }, []);
  useEffect(() => { try { setEmbedded(window.self !== window.top); } catch (_) { setEmbedded(true); } }, []);
  const toggleFullscreen = async () => { try { if (!document.fullscreenElement) await document.documentElement.requestFullscreen(); else await document.exitFullscreen(); } catch (_) {} };
  const location = useLocation();
  const isActive = (to) => {
    if (to === "/") return location.pathname === "/";
    if (to === "/cantieri") return location.pathname.startsWith("/cantieri") && location.pathname !== "/cantieri/nuovo";
    return location.pathname.startsWith(to);
  };

  return (
    <div className="relative min-h-dvh flex items-stretch bg-[#F8FAFC] overflow-x-hidden">
      {/* Sidebar */}
      <aside
        className={`absolute top-0 bottom-0 left-0 lg:sticky lg:top-0 lg:self-start z-50 min-h-full lg:h-dvh lg:min-h-dvh lg:max-h-dvh shrink-0 ${embedded ? "overflow-y-auto" : "overflow-hidden"} bg-[#0F172A] text-white flex flex-col transition-all duration-300 ${collapsed ? "lg:w-20" : "lg:w-72"} w-72 ${
          open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        <div className={`flex items-center h-16 shrink-0 border-b border-white/10 ${collapsed ? "lg:px-3 lg:justify-center" : "px-6"} px-6 gap-3`}>
          <div className="w-9 h-9 rounded-lg bg-orange-600 flex items-center justify-center">
            <HardHat className="w-5 h-5 text-white" />
          </div>
          <div className={collapsed ? "lg:hidden" : ""}>
            <div className="font-display font-bold text-base leading-none tracking-tight">Ellebierre S.R.L.</div>
            <div className="text-[10px] text-orange-400 font-mono tracking-widest uppercase mt-1">Gestionale Offerte e Cantieri</div>
          </div>
          <button type="button" onClick={() => setCollapsed((v) => !v)} title={collapsed ? "Espandi menu" : "Comprimi menu"} aria-label={collapsed ? "Espandi menu" : "Comprimi menu"} className={`hidden lg:inline-flex items-center justify-center rounded-lg p-2 text-slate-300 hover:bg-white/10 hover:text-white ${collapsed ? "ml-0" : "ml-auto"}`}>
            {collapsed ? <PanelLeftOpen className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
          </button>
          <button className="ml-auto lg:hidden" onClick={() => setOpen(false)} data-testid="sidebar-close-button">
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className={`${embedded ? "flex-none overflow-visible" : "flex-1 min-h-0 overflow-y-auto"} px-3 py-5 space-y-1`}>
          {NAV.map((n) => {
            if (n.permission !== "home" && !canMenu(session?.user, n.permission, n.permission === "offers" ? "list" : undefined)) return null;
            const Icon = n.icon;
            const active = isActive(n.to);
            return (
              <Link
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                data-testid={`nav-${n.to === "/" ? "personal-home" : n.to === "/offerte" ? "dashboard" : n.to.slice(1)}`}
                title={collapsed ? n.label : undefined}
                className={`flex items-center gap-3 rounded-lg text-sm font-medium transition-colors ${collapsed ? "lg:justify-center lg:px-2" : "px-4"} px-4 py-2.5 ${
                  active
                    ? "bg-orange-600 text-white"
                    : "text-slate-300 hover:bg-white/5 hover:text-white"
                }`}
              >
                <Icon className="w-[18px] h-[18px] shrink-0" />
                <span className={collapsed ? "lg:hidden" : ""}>{n.label}</span>
              </Link>
            );
          })}
        </nav>

        <div className={`shrink-0 border-t border-white/10 space-y-3 pb-[max(1rem,env(safe-area-inset-bottom))] ${collapsed ? "lg:p-2" : "p-4"} p-4`}>
          <div className={`rounded-xl border border-white/10 bg-white/5 px-3 py-3 ${collapsed ? "lg:hidden" : ""}`} data-testid="sidebar-current-user">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Collegato come</div>
            <div className="text-sm font-semibold text-white truncate">{session?.user?.name || "Amministratore"}</div>
            {session?.user?.email && <div className="text-[11px] text-slate-400 truncate">{session.user.email}</div>}
            <div className="mt-1.5 text-[10px] font-semibold text-emerald-400">
              {session?.auth_method === "google" ? "Google ✓" : "Accesso locale"}
            </div>
          </div>
          <button onClick={onLogout} title={collapsed ? "Esci" : undefined} data-testid="logout-button" className="w-full inline-flex items-center justify-center gap-1.5 text-xs text-slate-300 hover:text-white hover:bg-white/5 border border-white/10 rounded-lg py-2"><LogOut className="w-3.5 h-3.5" /><span className={collapsed ? "lg:hidden" : ""}>Esci</span></button>
          <div className={`text-[10px] text-slate-500 leading-relaxed ${collapsed ? "lg:hidden" : ""}`} data-testid="dev-credits">
            Sviluppo: <span className="text-slate-300 font-semibold">STECOSE</span> — Sviluppo Eccentrico<br />
            <a href="mailto:stecose70@gmail.com" className="hover:text-orange-400 underline-offset-2 hover:underline">stecose70@gmail.com</a>
          </div>
        </div>
      </aside>

      {open && <div className="fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0 overflow-x-hidden">
        <header className="sticky top-0 z-30 h-16 bg-slate-900 text-white flex items-center px-4 sm:px-6 gap-4">
          <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="sidebar-open-button">
            <Menu className="w-6 h-6" />
          </button>
          <div className="hidden sm:block">
            <span className="text-sm text-slate-300 font-display font-semibold" data-testid="header-title">Gestionale Offerte e Cantieri</span>
          </div>
          <div className="ml-auto flex items-center gap-2">
            {session?.user?.is_admin && <button
              onClick={async()=>{
                setSyncDocsBusy(true);
                try {
                  const start=await api.syncAllDocuments();
                  if (start.already_running) toast.info("Sincronizzazione documenti già in corso");
                  let last=null;
                  for (let i=0;i<600;i++) {
                    await new Promise(r=>setTimeout(r,1000));
                    last=await api.syncDocumentsStatus();
                    if (!last.running) break;
                  }
                  if (last?.error) throw new Error(last.error);
                  const r=last?.result||{};
                  const err=(r.errors||[]).length;
                  toast.success(`Sincronizzazione completata: ${r.classified||0} classificati senza AI · ${r.duplicates_removed||0} duplicati rimossi${err ? ` · ${err} avvisi` : ""}`);
                  window.dispatchEvent(new Event("ellebierre:documents-synced"));
                } catch(e) {
                  toast.error(e?.response?.data?.detail || e?.message || "Sincronizzazione documenti non riuscita");
                } finally { setSyncDocsBusy(false); }
              }}
              disabled={syncDocsBusy}
              title="Sincronizza Email e WhatsApp, rimuove duplicati e classifica i documenti"
              className="inline-flex items-center gap-1.5 bg-orange-600 hover:bg-orange-700 disabled:opacity-50 text-white text-xs font-semibold px-3 py-2 rounded-lg">
              {syncDocsBusy ? <Loader2 className="w-4 h-4 animate-spin"/> : <RefreshCw className="w-4 h-4"/>}
              <span className="hidden md:inline">{syncDocsBusy ? "Sincronizzo…" : "Sincronizza documenti"}</span>
            </button>}
            {fullscreenSupported && <button onClick={toggleFullscreen} title={isFullscreen ? "Esci da schermo intero" : "Schermo intero"} aria-label={isFullscreen ? "Esci da schermo intero" : "Schermo intero"} className="inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-white text-xs font-semibold px-3 py-2 rounded-lg">{isFullscreen ? <Minimize2 className="w-4 h-4"/> : <Maximize2 className="w-4 h-4"/>}<span className="hidden md:inline">{isFullscreen ? "Esci" : "Schermo intero"}</span></button>}

          </div>
        </header>

        <main className="flex-1 min-w-0 overflow-x-hidden px-4 sm:px-8 py-6 max-w-[1600px] w-full mx-auto">{children}</main>
      </div>
    </div>
  );
}
